import { addEdge, applyEdgeChanges, applyNodeChanges, Background, Connection, Controls, Edge, EdgeChange, Node, NodeChange, Panel, ReactFlow, ReactFlowProvider, useEdges, useReactFlow, MiniMap } from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Toolbar } from './menu'
import { toast, Toaster } from "sonner"
import { app } from '@/lib/app'
import { AGENT_SETTINGS_CHANGED_EVENT } from '@/lib/agents/escalationFlags'
import { workerRegistry } from '@/lib/agents/registry'
import { useTeamStore } from "@/lib/hooks/useTeam"
import { switchWorkspaceToAgentTeam } from "@/lib/hooks/useTeam"

import { ButtonEdge } from './edges'
import { RequestNode } from './nodes/input'
import { SchemaNode } from './nodes/schema'
import { ResponseNode } from './nodes/response'
// import { ConditionNode } from './nodes/condition'
import { TextNode } from './nodes/text'
import { AINode } from './nodes/ai'
import { STTNode } from './nodes/stt'
import { TTSNode } from './nodes/tts'
// import { BackgroundNode } from './nodes/backgroundstart'
import { Skeleton } from '../ui/skeleton'
import { supabase } from '@/lib/data'
import { agents } from '@/lib/agents'
import { CombineNode } from './nodes/combine'
import { display } from '@/lib/agents/workers/display'
import { DisplayNode } from './nodes/diisplay'
import { MockNode } from './nodes/mock'
import { useForceUpdate } from '@/lib/utils'
import { SearchNode } from './nodes/search'
import { CMSSearchNode } from './nodes/cmssearch'
import { AgentNode } from './nodes/agent'
import { ApiNode } from './nodes/api'
import { DocumentSelectorNode } from './nodes/documentselector'
import { StateNode } from './nodes/state'
import { MessageNode } from './nodes/message'
import { translate } from '@/lib/agents/workers/translate'
import { TranslateNode } from './nodes/translate'
import { PromptAgentNode } from './nodes/promptAgent'
import { HandoffAgentNode } from './nodes/handoffAgent'
import { TemplateNode } from './nodes/template'
import { ChatHistoryNode } from './nodes/chathistory'
import { ChatFlow } from './chat'
import { StructuredOutputNode } from './nodes/structuredoutput'
import { TooltipNode } from './nodes/tooltip'
import { DocumentGeneratorNode } from './nodes/documentgenerator'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Parentheses, BrainCog, Wrench, BugOff, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { WorkerConfigPanel } from './worker-config-panel'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { useUser } from '@/lib/hooks/useUser'
import { vectorSearch } from '@/lib/agents/workers/vectorsearch'
import { VectorSearchNode } from './nodes/vectorsearch'
import { OnboardNode } from './nodes/onboard'
import { LookupNode } from './nodes/lookup'
import { LanguageDetectionNode } from './nodes/languagedetection'
import { HITLNode } from './nodes/hitl'
import { VisionNode } from './nodes/vision'
import { ExternalApiConnectorNode } from './nodes/externalApiConnector'
import { ApiConnectorNode } from './nodes/apiConnector'
import { ExtractionNode } from './nodes/extraction'
import { WriteFlagsNode } from './nodes/writeflags'
import { FlowEditorAssistant } from "@/lib/assistant/modules/flowEditor"

// Function to generate rotating colors for icons
const getIconColor = (index: number) => {
  const colors = [
    'text-purple-600', // Purple
    'text-orange-600', // Orange
    'text-pink-600',   // Pink
    'text-blue-600',   // Blue
    'text-green-600',  // Green
    'text-red-600',    // Red
    'text-indigo-600', // Indigo
    'text-teal-600',   // Teal
    'text-yellow-600', // Yellow
    'text-cyan-600',   // Cyan
    'text-emerald-600', // Emerald
    'text-violet-600', // Violet
  ]
  return colors[index % colors.length]
}

// Function to generate background colors for icon containers
const getIconBackgroundColor = (index: number) => {
  const backgroundColors = [
    'bg-purple-100', // Light purple
    'bg-orange-100', // Light orange
    'bg-pink-100',   // Light pink
    'bg-blue-100',   // Light blue
    'bg-green-100',  // Light green
    'bg-red-100',    // Light red
    'bg-indigo-100', // Light indigo
    'bg-teal-100',   // Light teal
    'bg-yellow-100', // Light yellow
    'bg-cyan-100',   // Light cyan
    'bg-emerald-100', // Light emerald
    'bg-violet-100', // Light violet
  ]
  return backgroundColors[index % backgroundColors.length]
}

const nodeTypes = {
  request: RequestNode,
  schema: SchemaNode,
  response: ResponseNode,
  text: TextNode,
  ai: AINode,
  combine: CombineNode,
  display: DisplayNode,
  mock: MockNode,
  search: SearchNode,
  cmssearch: CMSSearchNode,
  agentWorker: AgentNode,
  api: ApiNode,
  documentSelector: DocumentSelectorNode,
  documentGenerator: DocumentGeneratorNode,
  state: StateNode,
  message: MessageNode,
  stt: STTNode,
  tts: TTSNode,
  translate: TranslateNode,
  promptAgent: PromptAgentNode,
  handoffAgent: HandoffAgentNode,
  template: TemplateNode,
  chatHistory: ChatHistoryNode,
  structured: StructuredOutputNode,
  tooltip: TooltipNode,
  vectorSearch: VectorSearchNode,
  onboard: OnboardNode,
  lookup: LookupNode,
  languageDetection: LanguageDetectionNode,
  hitl: HITLNode,
  vision: VisionNode,
  externalApiConnector: ExternalApiConnectorNode,
  apiConnector: ApiConnectorNode,
  extraction: ExtractionNode,
  writeFlags: WriteFlagsNode,
}

function Flow(props: {
  onAgentUpdate?: () => void
  onShowChat?: () => void
  onWorkerSelect?: (worker: AIWorker) => void
  onReady?: (rebuild: () => void, clear: () => void, removeWorker: (id: string) => void) => void
}) {

  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])
  const update = useForceUpdate()
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [colorUpdateTrigger, setColorUpdateTrigger] = useState(0)
  const counter = useRef(0)
  const { screenToFlowPosition } = useReactFlow()
  const { agent } = app

  function rebuildAgent() {
    const { agent } = app
    const initialNodes: Node[] = []
    for (const key in agent.workers) {
      const w = agent.workers[key]
      const newNode: Node = {
        id: w.config.id,
        data: {},
        type: w.config.type,
        position: { x: w.config.x, y: w.config.y },
      }

      //ToDo: Fix me
      if (w.type === "ai" || w.type === "promptAgent" || w.type === "handoffAgent") {
        if (w.config.width && w.config.height) {
          newNode.width = w.config.width
          newNode.height = w.config.height
        }
      }
      initialNodes.push(newNode)
    }
    setNodes(initialNodes)
    const initialEdges: Edge[] = []
    for (const key in agent.edges) {
      const e = agent.edges[key]
      initialEdges.push({ id: key, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, type: "customEdge" })
    }
    setEdges(initialEdges)
    update()
  }

  function clearCanvas() {
    setNodes([])
    setEdges([])
  }

  function removeWorkerNode(workerId: string) {
    setNodes(ns => ns.filter(n => n.id !== workerId))
    setEdges(es => es.filter(e => e.source !== workerId && e.target !== workerId))
    setTimeout(() => {
      app.agent.deleteWorker(workerId)
      app.agent.update()
      if (props.onAgentUpdate) props.onAgentUpdate()
    }, 0)
  }

  useEffect(() => {
    rebuildAgent()
    props.onReady?.(rebuildAgent, clearCanvas, removeWorkerNode)
  }, [])

  // Add global drop handler for debugging
  useEffect(() => {
    const handleGlobalDrop = (event: DragEvent) => {
      console.log("Global drop event!")
      console.log("Global drop dataTransfer types:", event.dataTransfer?.types)
      if (event.dataTransfer) {
        console.log("Global drop nodeType:", event.dataTransfer.getData("nodeType"))
      }
    }

    document.addEventListener('drop', handleGlobalDrop)
    return () => document.removeEventListener('drop', handleGlobalDrop)
  }, [])

  agent.update = () => {
    counter.current++
    setNodes((nds) =>
      nds.map((node) => {
        const worker = app.agent.workers[node.id]
        if (!worker) {
          return node
        }
        return {
          ...node,
          data: {
            ...node.data,
            label: counter.current.toString(),
          },
        }
      }),
    )
    if (props.onAgentUpdate) props.onAgentUpdate()
  }

  const onNodesChange = (changes: NodeChange[]) => {
    for (const change of changes) {
      if (change.type === 'position') {
        const w = app.agent.workers[change.id]
        if (!w) continue
        w.config.x = change.position.x
        w.config.y = change.position.y
      }
      if (change.type == "dimensions") {
        const w = app.agent.workers[change.id]
        if (!w) continue
        if (!change.resizing) {
          w.config.width = change.dimensions.width
          w.config.height = change.dimensions.height
        }
      }
    }
    setNodes((nds) => applyNodeChanges(changes, nds))
  }

  const onEdgesChange = (changes: EdgeChange[]) => {
    let hasRemovals = false
    for (const change of changes) {
      if (change.type === 'remove') {
        delete app.agent.edges[change.id]
        hasRemovals = true
      }
    }

    setEdges((eds) => applyEdgeChanges(changes, eds))

    if (!hasRemovals) {
      agent.updateWorkers()
      agent.update()
    }
  }

  const onConnect = (c: Connection) => {
    // console.log("Connect:", c)
    const worker = app.agent.workers[c.source]
    // const handle = worker.handles[c.sourceHandle]
    worker.updateWorker()
    c = { ...c, type: 'customEdge' } as any
    setEdges((eds) => {
      const added = addEdge(c, eds)
      for (const edge of added) {
        app.agent.edges[edge.id] = edge
      }
      return added
    })
    agent.updateWorkers()
    agent.update()
  }

  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    const worker = app.agent.workers[node.id]
    if (worker && props.onWorkerSelect) {
      props.onWorkerSelect(worker)
    }
  }, [props.onWorkerSelect])

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'move'
    console.log("DragOver event on ReactFlow")
    // Add visual feedback
    event.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'
  }, [])

  const onDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.currentTarget.style.backgroundColor = ''
  }, [])

  const onDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    console.log("Drop event triggered!")
    console.log("Event target:", event.target)
    console.log("Event currentTarget:", event.currentTarget)
    event.preventDefault()
    event.stopPropagation()

    // Reset background color
    event.currentTarget.style.backgroundColor = ''

    const nodeType = event.dataTransfer.getData("nodeType")
    console.log("Drop event received, nodeType:", nodeType)
    console.log("All dataTransfer types:", event.dataTransfer.types)
    console.log("All dataTransfer items:", event.dataTransfer.items)

    if (!nodeType) {
      console.log("No nodeType found in drop event")
      return
    }

    const type = nodeType as WorkerTypes
    console.log("Creating node of type:", type)

    if (type == "request" && app.agent.hasInput()) {
      toast("Only one Input is allowed.", {
        action: { label: "Ok", onClick: null, },
      })
      return
    }

    if (type == "response" && app.agent.hasResponse()) {
      toast("Only one Response is allowed.", {
        action: { label: "Ok", onClick: null, },
      })
      return
    }

    const factory = workerRegistry[type] as WorkerRegistryItem
    if (!factory) {
      console.log("No factory found for type:", type)
      toast("Unknown node type: " + type)
      return
    }

    try {
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY, })
      console.log("Drop position:", position)

      const worker = factory.create(app.agent)
      worker.config.x = position.x
      worker.config.y = position.y

      const node = {
        id: worker.config.id,
        type: worker.config.type,
        position,
        data: {}
      }

      console.log("Adding node:", node)
      setNodes((nds) => nds.concat(node))
      toast(`Added ${factory.title} to flow`)
    } catch (error) {
      console.error("Error creating node:", error)
      toast("Error creating node: " + error.message)
    }
  }, [screenToFlowPosition])

  const onDelete = useCallback(({ nodes }: { nodes: Node[]; edges: Edge[] }): void => {
    if (nodes && nodes.length > 0) {
      for (const node of nodes) {
        app.agent.deleteWorker(node.id)
      }
    }
  }, [])

  const addNodeToFront = (nodeType: string) => {
    const factory = workerRegistry[nodeType] as WorkerRegistryItem
    if (!factory) {
      console.error("No factory found for type:", nodeType)
      toast("Unknown node type: " + nodeType)
      return
    }

    try {
      const position = screenToFlowPosition({ x: 100, y: 100 }) // Example position
      const worker = factory.create(app.agent)
      worker.config.x = position.x
      worker.config.y = position.y

      const node = {
        id: worker.config.id,
        type: worker.config.type,
        position,
        data: {}
      }

      setNodes((nds) => [node, ...nds])
      toast(`Added ${factory.title} to front of flow`)
    } catch (error) {
      console.error("Error adding node to front:", error)
      toast("Error adding node to front: " + error.message)
    }
  }

  const orphanedEdges = useMemo(() => {
    return edges.filter(e => {
      const src = agent.workers[e.source]
      const tgt = agent.workers[e.target]
      if (!src || !tgt) return true
      if (!src.handles[e.sourceHandle]) return true
      if (!tgt.handles[e.targetHandle]) return true
      return false
    })
  }, [edges])

  function clearOrphanedEdges() {
    const orphanIds = new Set(orphanedEdges.map(e => e.id))
    orphanIds.forEach(id => delete agent.edges[id])
    setEdges(eds => eds.filter(e => !orphanIds.has(e.id)))
  }

  return <div className='flex-1 h-full'>
    <div
      className="w-full h-full"
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDelete={onDelete}
        onNodeDoubleClick={onNodeDoubleClick}
        className={`bg-sky-50!`}
        style={{ width: '100%', height: '100%' }}
        nodeTypes={nodeTypes}
        edgeTypes={{ customEdge: ButtonEdge }}
        fitView
        minZoom={0.1}
      >
        <Background />
        <Controls />
        <MiniMap />
        <Panel position="top-left" className="bg-transparent! border-none! shadow-none!">
          <div className="absolute left-0 top-12 flex flex-col space-y-1 bg-slate-50 p-2 rounded-md border border-slate-300">
            <DropdownMenu open={openDropdown === 'io'} onOpenChange={(open) => setOpenDropdown(open ? 'io' : null)}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100" title="Input & Output">
                  <Parentheses size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" className="w-[480px]" sideOffset={0}>
                <div className="p-2">
                  <h3 className="font-semibold text-base text-gray-900">Input & Output</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1 p-2">
                    {Object.entries(workerRegistry).filter(([key, node]) => node.category == "io").map(([key, node], index) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 p-2 cursor-move hover:bg-accent rounded-sm select-none"
                        draggable
                        onDragStart={(event) => {
                          console.log('Drag start for IO:', key)
                          event.dataTransfer.setData('nodeType', key)
                          event.dataTransfer.effectAllowed = 'move'
                          // Set a visual indicator
                          event.dataTransfer.setDragImage(event.currentTarget, 0, 0)
                          console.log('Drag started for:', key)
                          // Close the dropdown when dragging starts
                          setOpenDropdown(null)
                        }}
                        onDragEnd={(event) => {
                          console.log('Drag end for IO:', key)
                        }}
                        onClick={(event) => {
                          // Quick click - add to front of flow
                          console.log('Quick click - adding to front:', key)
                          addNodeToFront(key)
                        }}
                      >
                        <div className={`${getIconBackgroundColor(index)} rounded-md p-1`}>
                          <node.icon size={16} className={getIconColor(index)} />
                        </div>
                        <div>
                          <div className="font-medium">{node.title}</div>
                          <div className="text-xs text-muted-foreground">{node.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu open={openDropdown === 'generator'} onOpenChange={(open) => setOpenDropdown(open ? 'generator' : null)}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100" title="Generators">
                  <BrainCog size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" className="w-[480px]" sideOffset={0}>
                <div className="p-2">
                  <h3 className="font-semibold text-base text-gray-900">Generators</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1 p-2">
                    {Object.entries(workerRegistry).filter(([key, node]) => node.category == "generator" && node.type !== "schema").map(([key, node], index) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 p-2 cursor-move hover:bg-accent rounded-sm select-none"
                        draggable
                        onDragStart={(event) => {
                          console.log('Drag start for Generator:', key)
                          event.dataTransfer.setData('nodeType', key)
                          event.dataTransfer.effectAllowed = 'move'
                          // Set a visual indicator
                          event.dataTransfer.setDragImage(event.currentTarget, 0, 0)
                          console.log('Drag started for:', key)
                          // Close the dropdown when dragging starts
                          setOpenDropdown(null)
                        }}
                        onDragEnd={(event) => {
                          console.log('Drag end for Generator:', key)
                        }}
                        onClick={(event) => {
                          // Quick click - add to front of flow
                          console.log('Quick click - adding to front:', key)
                          addNodeToFront(key)
                        }}
                      >
                        <div className={`${getIconBackgroundColor(index)} rounded-md p-1`}>
                          <node.icon size={16} className={getIconColor(index)} />
                        </div>
                        <div>
                          <div className="font-medium">{node.title}</div>
                          <div className="text-xs text-muted-foreground">{node.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu open={openDropdown === 'tool'} onOpenChange={(open) => setOpenDropdown(open ? 'tool' : null)}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100" title="Tools">
                  <Wrench size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" className="w-[480px]" sideOffset={0}>
                <div className="p-2">
                  <h3 className="font-semibold text-base text-gray-900">Tools</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1 p-2">
                    {Object.entries(workerRegistry).filter(([key, node]) => node.category == "tool").map(([key, node], index) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 p-2 cursor-move hover:bg-accent rounded-sm select-none"
                        draggable
                        onDragStart={(event) => {
                          console.log('Drag start for Tool:', key)
                          event.dataTransfer.setData('nodeType', key)
                          event.dataTransfer.effectAllowed = 'move'
                          // Set a visual indicator
                          event.dataTransfer.setDragImage(event.currentTarget, 0, 0)
                          console.log('Drag started for:', key)
                          // Close the dropdown when dragging starts
                          setOpenDropdown(null)
                        }}
                        onDragEnd={(event) => {
                          console.log('Drag end for Tool:', key)
                        }}
                        onClick={(event) => {
                          // Quick click - add to front of flow
                          console.log('Quick click - adding to front:', key)
                          addNodeToFront(key)
                        }}
                      >
                        <div className={`${getIconBackgroundColor(index)} rounded-md p-1`}>
                          <node.icon size={16} className={getIconColor(index)} />
                        </div>
                        <div>
                          <div className="font-medium">{node.title}</div>
                          <div className="text-xs text-muted-foreground">{node.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu open={openDropdown === 'debug'} onOpenChange={(open) => setOpenDropdown(open ? 'debug' : null)}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100" title="Debug">
                  <BugOff size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" className="w-[480px]" sideOffset={0}>
                <div className="p-2">
                  <h3 className="font-semibold text-base text-gray-900">Debug</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1 p-2">
                    {Object.entries(workerRegistry).filter(([key, node]) => node.category == "debug").map(([key, node], index) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 p-2 cursor-move hover:bg-accent rounded-sm select-none"
                        draggable
                        onDragStart={(event) => {
                          console.log('Drag start for Debug:', key)
                          event.dataTransfer.setData('nodeType', key)
                          event.dataTransfer.effectAllowed = 'move'
                          // Set a visual indicator
                          event.dataTransfer.setDragImage(event.currentTarget, 0, 0)
                          console.log('Drag started for:', key)
                          // Close the dropdown when dragging starts
                          setOpenDropdown(null)
                        }}
                        onDragEnd={(event) => {
                          console.log('Drag end for Debug:', key)
                        }}
                        onClick={(event) => {
                          // Quick click - add to front of flow
                          console.log('Quick click - adding to front:', key)
                          addNodeToFront(key)
                        }}
                      >
                        <div className={`${getIconBackgroundColor(index)} rounded-md p-1`}>
                          <node.icon size={16} className={getIconColor(index)} />
                        </div>
                        <div>
                          <div className="font-medium">{node.title}</div>
                          <div className="text-xs text-muted-foreground">{node.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Panel>
        <Panel position="top-right" className="bg-transparent! border-none! shadow-none!">
          <Toolbar onShowChat={props.onShowChat} rebuild={rebuildAgent} />
        </Panel>
        {orphanedEdges.length > 0 && (
          <Panel position="bottom-center" className="bg-transparent! border-none! shadow-none!">
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 shadow-sm text-xs text-amber-800">
              <TriangleAlert className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span>{orphanedEdges.length} edge{orphanedEdges.length > 1 ? 's' : ''} reference handles that no longer exist and can't be rendered.</span>
              <button
                onClick={clearOrphanedEdges}
                className="ml-1 underline font-medium hover:text-amber-900"
              >
                Clear them
              </button>
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  </div>

}

export function FlowDesigner({ id }: { id?: string }) {

  const isLoading = useRef(false)
  const rebuildRef = useRef<(() => void) | null>(null)
  /** Empties the canvas before the agent underneath it is swapped. */
  const clearRef = useRef<(() => void) | null>(null)
  /** Removes a node from the canvas before its worker is deleted. */
  const removeRef = useRef<((id: string) => void) | null>(null)
  const [agent, setAgent] = useState<Agent>(null)
  const [conversational, setConversational] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [selectedWorker, setSelectedWorker] = useState<AIWorker | null>(null)
  const [showConfigPanel, setShowConfigPanel] = useState(false)
  const [chatHistory, setChatHistory] = useState<ChatHistory>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [availableConversations, setAvailableConversations] = useState<Array<{ uid: string; lastMessage: string; timestamp: string }>>([])
  const [selectedConversationUid, setSelectedConversationUid] = useState<string | null>(null)
  const [isNewConversation, setIsNewConversation] = useState(false)
  const update = useForceUpdate()
  const { data: user } = useUser()

  // Update isNewConversation when availableConversations changes
  useEffect(() => {
    if (selectedConversationUid && availableConversations.length > 0) {
      const exists = availableConversations.some(c => c.uid === selectedConversationUid)
      if (exists && isNewConversation) {
        setIsNewConversation(false)
      } else if (!exists && !isNewConversation && chatHistory.length === 0) {
        // If conversation doesn't exist in list and no history, it's new
        setIsNewConversation(true)
      }
    }
  }, [availableConversations, selectedConversationUid])

  const extractTextContent = (content: any): string => {
    if (typeof content === 'string') return content

    if (Array.isArray(content)) {
      return content
        .map((item: any) => {
          if (typeof item === 'string') return item
          if (item?.text) return item.text
          if (item?.content) return item.content
          if (item?.message) return item.message
          return ''
        })
        .filter(Boolean)
        .join('\n')
    }

    if (content?.text) return content.text
    if (content?.content) return content.content
    if (content?.message) return content.message

    return JSON.stringify(content)
  }

  const loadAvailableConversations = async (agentId: string) => {
    if (!user?.id) return

    try {
      const { data: conversations, error } = await supabase
        .from('history')
        .select('uid, created_at, content, payload, role')
        .eq('agent', agentId)
        .not('uid', 'is', null)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error loading available conversations:', error)
        return
      }

      if (!conversations || conversations.length === 0) {
        setAvailableConversations([])
        return
      }

      // Group by uid and get the latest message for each conversation
      const conversationMap = new Map<string, { lastMessage: string; timestamp: string }>()

      conversations.forEach(record => {
        const uid = record.uid
        if (!uid) return

        let content = ''
        if (record.payload && typeof record.payload === 'object' && 'content' in record.payload) {
          content = extractTextContent((record.payload as any).content)
        } else if (record.content) {
          content = extractTextContent(record.content)
        }

        if (!conversationMap.has(uid) || new Date(record.created_at) > new Date(conversationMap.get(uid)!.timestamp)) {
          conversationMap.set(uid, {
            lastMessage: content || 'No content',
            timestamp: record.created_at
          })
        }
      })

      const conversationsList = Array.from(conversationMap.entries())
        .map(([uid, data]) => ({
          uid,
          lastMessage: data.lastMessage.substring(0, 50) + (data.lastMessage.length > 50 ? '...' : ''),
          timestamp: data.timestamp
        }))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

      setAvailableConversations(conversationsList)
    } catch (error) {
      console.error('Error loading available conversations:', error)
    }
  }

  const loadAgentConversationHistory = async (agentId: string, teamId: string, conversationUid?: string) => {
    if (!user?.id) return

    const uidToLoad = conversationUid || app.agent?.debuguuid
    if (!uidToLoad) {
      setChatHistory([])
      return
    }

    setIsLoadingHistory(true)
    try {
      const { data: historyRecords, error } = await supabase
        .from('history')
        .select('*')
        .eq('agent', agentId)
        .eq('uid', uidToLoad)
        .order('created_at', { ascending: true })
        .limit(100)

      if (error) {
        console.error('Error loading agent conversation history:', error)
        return
      }

      if (!historyRecords || historyRecords.length === 0) {
        setChatHistory([])
        return
      }

      const sortedRecords = historyRecords
        .filter(record => record.content)
        .sort((a, b) => {
          // Primary sort by created_at timestamp
          const timeA = new Date(a.created_at).getTime()
          const timeB = new Date(b.created_at).getTime()
          if (timeA !== timeB) {
            return timeA - timeB
          }
          // Secondary sort by id to ensure stable ordering when timestamps are equal
          return a.id - b.id
        })

      const chatHistory: ChatHistory = sortedRecords.map(record => {
        let content = ''
        if (record.payload && typeof record.payload === 'object' && 'content' in record.payload) {
          content = extractTextContent((record.payload as any).content)
        } else if (record.content) {
          content = extractTextContent(record.content)
        }

        return {
          role: record.role === 'user' ? 'user' : 'assistant',
          content: content || 'No content available'
        }
      })

      setChatHistory(chatHistory)
    } catch (error) {
      console.error('Error loading agent conversation history:', error)
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const handleConversationSelect = async (conversationUid: string, agentId: string, teamId: string) => {
    setIsNewConversation(false)
    setSelectedConversationUid(conversationUid)
    if (app.agent) {
      app.agent.debuguuid = conversationUid
      // Save the updated debuguuid to the database so it persists
      if (app.agent.id) {
        await supabase
          .from('agents')
          .update({ debuguuid: conversationUid })
          .eq('id', app.agent.id)
      }
    }
    await loadAgentConversationHistory(agentId, teamId, conversationUid)
  }

  const handleNewConversation = async (agentId: string, teamId: string) => {
    const newUuid = crypto.randomUUID()
    setIsNewConversation(true)
    setSelectedConversationUid(newUuid)
    if (app.agent) {
      app.agent.debuguuid = newUuid
      // Save the new debuguuid to the database so it persists
      if (app.agent.id) {
        await supabase
          .from('agents')
          .update({ debuguuid: newUuid })
          .eq('id', app.agent.id)
      }
    }
    setChatHistory([])
  }

  useEffect(() => {
    // console.log("Loading agent:", id)
    if (isLoading.current) return
    isLoading.current = true
    if (id == "new") {
      app.agent = agents.createAgent({
        title: "Create Agent",
      })
      setAgent(app.agent)
    } else {
      app.state.agentLoading = true
      const selectedTeam = useTeamStore.getState().selectedTeam
      if (!selectedTeam?.id) {
        console.error("No team selected")
        app.state.agentLoading = false
        return
      }
      agents.loadAgent(id as any, selectedTeam.id).then(async (a) => {
        if (!a) {
          console.error(`Agent ${id} not found or not accessible for current team`)
          const team = await switchWorkspaceToAgentTeam(Number(id))
          if (team?.id) {
            try {
              const retry = await agents.loadAgent(id as any, team.id)
              if (retry) {
                app.agent = retry
                setAgent(retry)
                await loadAvailableConversations(id)
                const currentUid = app.agent?.debuguuid
                if (currentUid) {
                  // Check if this conversation exists in the database
                  const { data: conversations } = await supabase
                    .from('history')
                    .select('uid')
                    .eq('agent', id)
                    .eq('uid', currentUid)
                    .limit(1)

                  if (conversations && conversations.length > 0) {
                    setSelectedConversationUid(currentUid)
                    setIsNewConversation(false)
                    await loadAgentConversationHistory(id, team.id)
                  } else {
                    setIsNewConversation(true)
                    setSelectedConversationUid(currentUid)
                    setChatHistory([])
                  }
                } else {
                  setIsNewConversation(true)
                }
              }
            } catch (e) {
              console.error("Retry load after workspace switch failed:", e)
            }
          }
          app.state.agentLoading = false
          return
        }
        app.agent = a
        setAgent(a)
        await loadAvailableConversations(id)
        const currentUid = app.agent?.debuguuid
        if (currentUid) {
          // After loading conversations, check if current UID exists
          const { data: conversations } = await supabase
            .from('history')
            .select('uid')
            .eq('agent', id)
            .eq('uid', currentUid)
            .limit(1)

          if (conversations && conversations.length > 0) {
            setSelectedConversationUid(currentUid)
            setIsNewConversation(false)
            await loadAgentConversationHistory(id, selectedTeam.id)
          } else {
            setIsNewConversation(true)
            setSelectedConversationUid(currentUid)
            setChatHistory([])
          }
        } else {
          setIsNewConversation(true)
        }
        app.state.agentLoading = false
      }).catch((error) => {
        console.error("Error loading agent:", error)
        app.state.agentLoading = false
      })
    }
  }, [])

  if (!agent) return <div className='size-full p-4'>
    <div className="space-y-2">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="w-full h-96" />
    </div>
  </div>

  function onAgentUpdate() {
    // console.log("Agent updated, isConversational: ", app.agent.isConversational)
    setConversational(app.agent.isConversational)
    update()
  }

  function onShowChat() {
    if (showChat) {
      setShowChat(false)
      setShowConfigPanel(false)
      setSelectedWorker(null)
    } else {
      setShowChat(true)
      setShowConfigPanel(true)
      setSelectedWorker(null)
      // Reload conversations when opening chat panel
      if (agent?.id) {
        loadAvailableConversations(agent.id.toString())
      }
    }
  }

  function onWorkerSelect(worker: AIWorker) {
    setSelectedWorker(worker)
    setShowConfigPanel(true)
  }

  function onCloseWorkerPanel() {
    setSelectedWorker(null)
    setShowConfigPanel(false)
  }

  return <>
    <ReactFlowProvider>
      <ResizablePanelGroup direction="horizontal" className="w-full h-full">
        <ResizablePanel id="flow-main" order={1} defaultSize={showConfigPanel ? 70 : 100}>
          {/*
            The builder assistant floats over the canvas. Mounted inside this
            panel so its absolute positioning is relative to the flow area
            rather than the whole window, and so it moves with the panel when
            the config drawer opens.
          */}
          <div className="relative h-full w-full">
            <Flow
              onAgentUpdate={onAgentUpdate}
              onShowChat={onShowChat}
              onWorkerSelect={onWorkerSelect}
              onReady={(rebuild, clear, remove) => {
                rebuildRef.current = rebuild
                clearRef.current = clear
                removeRef.current = remove
              }}
            />
            <FlowEditorAssistant
              agent={agent}
              onGraphChanged={() => {
                rebuildRef.current?.()
                agent.update()
              }}
              onAgentReplaced={next => {
                clearRef.current?.()
                setTimeout(() => {
                  // onLoadVersion): assign app.agent, then rebuild from it.
                  app.agent = next
                  // The settings dialog holds its own copy of flags and forms; reload it from the new agent
                  window.dispatchEvent(new Event(AGENT_SETTINGS_CHANGED_EVENT))
                  setAgent(next)
                  rebuildRef.current?.()
                  next.update()
                }, 0)
              }}
              onWorkerRemoved={id => removeRef.current?.(id)}
            />
          </div>
        </ResizablePanel>
        {showConfigPanel && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel id="flow-config" order={2} defaultSize={30} minSize={10} maxSize={85}>
              <WorkerConfigPanel
                selectedWorker={selectedWorker}
                onClose={onCloseWorkerPanel}
                showChat={showChat}
                chatHistory={chatHistory}
                onChatHistoryChange={async (newHistory) => {
                  setChatHistory(newHistory)
                  if (isNewConversation && newHistory.length > 0 && agent?.id && selectedConversationUid) {
                    setTimeout(async () => {
                      await loadAvailableConversations(agent.id.toString())
                    }, 500)
                  }
                }}
                isLoadingHistory={isLoadingHistory}
                availableConversations={availableConversations}
                selectedConversationUid={selectedConversationUid}
                isNewConversation={isNewConversation}
                onConversationSelect={(uid) => {
                  const selectedTeam = useTeamStore.getState().selectedTeam
                  if (agent?.id && selectedTeam?.id) {
                    if (uid === 'new') {
                      handleNewConversation(agent.id.toString(), selectedTeam.id)
                    } else {
                      handleConversationSelect(uid, agent.id.toString(), selectedTeam.id)
                    }
                  }
                }}
                agentId={agent?.id?.toString()}
              />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
      <Toaster />
    </ReactFlowProvider>
  </>

}
